
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { UserPlus, Users, Contact } from "lucide-react";
import { toast } from "sonner";
import CrewMemberForm from "./crew/CrewMemberForm";
import CrewMemberCard from "./crew/CrewMemberCard";
import { CrewMember, CrewRole, CREW_ROLES } from "@/types/crew";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/components/auth/AuthProvider";
import ContactForm from "@/components/contacts/ContactForm";

interface CrewListProps {
  projectId: string;
  canEdit: boolean;
  isPublicView?: boolean;
}

export default function CrewList({ projectId, canEdit, isPublicView = false }: CrewListProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<CrewMember | null>(null);
  const [showContactsDialog, setShowContactsDialog] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: crewMembers, isLoading: isLoadingCrew, error } = useQuery({
    queryKey: ["crew-members", projectId],
    queryFn: async () => {
      console.log("Fetching crew members for project:", projectId);
      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("project_id", projectId)
        .order("role");

      if (error) {
        console.error("Error fetching crew members:", error);
        toast.error("Failed to load crew members");
        throw error;
      }

      console.log("Crew members fetched:", data);
      return data as CrewMember[];
    },
  });

  const { data: contacts, isLoading: isLoadingContacts } = useQuery({
    queryKey: ["crew-contacts", user?.id],
    queryFn: async () => {
      console.log("Fetching contacts for user:", user?.id);
      const { data, error } = await supabase
        .from("crew_contacts")
        .select("*")
        .eq("user_id", user?.id)
        .order("name");

      if (error) {
        console.error("Error fetching contacts:", error);
        toast.error("Failed to load contacts");
        throw error;
      }

      console.log("Contacts fetched:", data);
      return data;
    },
    enabled: (showContactsDialog || showNewContactForm) && !!user,
  });

  const handleSubmit = async (member: Partial<CrewMember>) => {
    try {
      setIsLoading(true);
      
      if (editingMember) {
        const { error } = await supabase
          .from("crew_members")
          .update({
            name: member.name,
            role: member.role,
            phone: member.phone,
            email: member.email,
            notes: member.notes,
          })
          .eq("id", editingMember.id);

        if (error) throw error;
        toast.success("Crew member updated successfully");
      } else {
        console.log("Adding new crew member:", member);
        const { data, error } = await supabase.from("crew_members").insert([
          {
            project_id: projectId,
            name: member.name,
            role: member.role,
            phone: member.phone || null,
            email: member.email || null,
            notes: member.notes || null,
          },
        ]).select();

        if (error) throw error;
        console.log("Added crew member response:", data);
        toast.success("Crew member added successfully");
      }

      queryClient.invalidateQueries({ queryKey: ["crew-members", projectId] });
      handleClose();
    } catch (error) {
      console.error("Error saving crew member:", error);
      toast.error("Failed to save crew member");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("crew_members")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Crew member removed successfully");
      queryClient.invalidateQueries({ queryKey: ["crew-members", projectId] });
    } catch (error) {
      toast.error("Failed to remove crew member");
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingMember(null);
    setShowContactsDialog(false);
    setShowNewContactForm(false);
    setSelectedContactId(null);
  };

  const handleEdit = (member: CrewMember) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const handleContactSelect = async (contact: any) => {
    try {
      setIsLoading(true);
      setSelectedContactId(contact.id);
      
      // Ensure the role is valid
      const role: CrewRole = CREW_ROLES.includes(contact.role as CrewRole) 
        ? (contact.role as CrewRole) 
        : "Other";

      // Prepare the crew member data
      const newMember = {
        project_id: projectId,
        name: contact.name,
        role: role,
        phone: contact.phone || null,
        email: contact.email || null,
        notes: contact.notes || null,
        rate: contact.rate || null,
        rate_type: contact.rate_type || null
      };

      console.log("Adding contact to crew:", newMember);

      const { data, error } = await supabase
        .from("crew_members")
        .insert(newMember)
        .select();

      if (error) {
        console.error("Error adding contact to crew:", error);
        toast.error(`Failed to add contact to crew: ${error.message}`);
        return;
      }
      
      console.log("Added crew member response:", data);
      
      if (data && data.length > 0) {
        toast.success("Contact added to crew successfully");
        queryClient.invalidateQueries({ queryKey: ["crew-members", projectId] });
        setShowContactsDialog(false);
      } else {
        console.warn("Insert succeeded but no data was returned");
        toast.success("Contact added to crew successfully");
        queryClient.invalidateQueries({ queryKey: ["crew-members", projectId] });
        setShowContactsDialog(false);
      }
    } catch (error: any) {
      console.error("Error adding contact to crew:", error);
      toast.error(`Failed to add contact to crew: ${error.message || "Unknown error"}`);
    } finally {
      setIsLoading(false);
      setSelectedContactId(null);
    }
  };

  const handleNewContactSubmit = async (contactData: any) => {
    try {
      setIsLoading(true);
      
      // Create the new contact first
      const { data: newContact, error: contactError } = await supabase
        .from("crew_contacts")
        .insert({
          user_id: user?.id,
          name: contactData.name,
          role: contactData.role,
          phone: contactData.phone,
          email: contactData.email,
          notes: contactData.notes,
        })
        .select()
        .single();

      if (contactError) {
        console.error("Error creating contact:", contactError);
        toast.error("Failed to create contact");
        return;
      }

      // Invalidate contacts query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["crew-contacts", user?.id] });
      
      // Now add the contact to the crew
      await handleContactSelect(newContact);
      
      setShowNewContactForm(false);
    } catch (error: any) {
      console.error("Error creating contact:", error);
      toast.error(`Failed to create contact: ${error.message || "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
        <h3 className="text-xl font-semibold text-white">Crew Members</h3>
        {canEdit && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={() => setShowContactsDialog(true)}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-0 h-auto py-3 px-4 w-full sm:w-auto"
              size="lg"
              disabled={isLoading}
            >
              <Users className="mr-2 h-5 w-5" />
              Add from Contacts
            </Button>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-0 h-auto py-3 px-4 w-full sm:w-auto"
              size="lg"
              disabled={isLoading}
            >
              <UserPlus className="mr-2 h-5 w-5" />
              Add Crew Member
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {crewMembers?.map((member) => (
          <CrewMemberCard
            key={member.id}
            member={member}
            onEdit={canEdit ? handleEdit : undefined}
            onDelete={canEdit ? () => handleDelete(member.id) : undefined}
          />
        ))}
        
        {isLoadingCrew && (
          <div className="col-span-2 text-center py-8">
            <div className="animate-pulse text-muted-foreground">Loading crew members...</div>
          </div>
        )}
        
        {!isLoadingCrew && crewMembers?.length === 0 && (
          <div className="col-span-2 text-center py-8">
            <div className="text-muted-foreground">No crew members yet. Add your first crew member.</div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <CrewMemberForm
            onClose={handleClose}
            onSubmit={handleSubmit}
            initialData={editingMember || undefined}
            projectId={projectId}
          />
        </div>
      )}

      <Dialog open={showContactsDialog} onOpenChange={setShowContactsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add from Contacts</DialogTitle>
            <DialogDescription>
              Select a contact to add to your crew list, or create a new one
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setShowNewContactForm(true)}
              variant="outline"
              disabled={isLoading}
            >
              <Contact className="mr-2 h-4 w-4" />
              Create New Contact
            </Button>
          </div>
          
          {isLoadingContacts && (
            <div className="py-8 text-center">
              <div className="animate-pulse text-muted-foreground">Loading contacts...</div>
            </div>
          )}
          
          <div className="grid gap-4 sm:grid-cols-2 max-h-[60vh] overflow-y-auto">
            {contacts?.map((contact) => (
              <div
                key={contact.id}
                className={`p-4 border rounded-lg hover:border-primary transition-colors cursor-pointer ${
                  isLoading && selectedContactId === contact.id
                    ? 'opacity-50 bg-muted'
                    : 'hover:bg-accent/20'
                }`}
                onClick={() => !isLoading && handleContactSelect(contact)}
              >
                <h4 className="font-semibold">{contact.name}</h4>
                <p className="text-sm text-muted-foreground">{contact.role}</p>
                {contact.email && (
                  <p className="text-sm text-muted-foreground">{contact.email}</p>
                )}
                {contact.phone && (
                  <p className="text-sm text-muted-foreground">{contact.phone}</p>
                )}
                {isLoading && selectedContactId === contact.id && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Adding to crew...
                  </div>
                )}
              </div>
            ))}
            
            {contacts?.length === 0 && !isLoadingContacts && (
              <div className="col-span-2 text-center py-8">
                <p className="text-muted-foreground">No contacts found. Create your first contact above.</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowContactsDialog(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showNewContactForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <ContactForm
            onClose={() => setShowNewContactForm(false)}
            onSubmit={handleNewContactSubmit}
          />
        </div>
      )}
    </div>
  );
}
